import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchUserCircles, isCircleVisible } from "@core/features/circle";
import { usePlatformStore } from "@platform/store";
import { useCurrentMeeting } from "@platform/hooks/useCurrentMeeting";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { hasJoinableMeeting } from "@platform/utils/hasJoinableMeeting";
import { getNextUpcomingMeeting } from "@platform/utils/getNextUpcomingMeeting";
import { getClosestMeetingToCurrentTime } from "@platform/utils/getClosestMeetingToCurrentTime";
import { getUserMeetingRoleFromMeeting } from "@platform/features/programPhases/utils";
import { SchedulingCohortMessageContainer } from "@platform/features/programScheduling";
import { CohortDashboardBanner } from "./CohortDashboardBanner";

export function CohortDashboardBannerContainer() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const programId = id ? Number(id) : undefined;
  const user = usePlatformStore((s) => s.user);

  const { data: circlesData } = useFetchUserCircles({ user });
  const visibleCircle = useMemo(
    () => (circlesData?.circles ?? []).find(isCircleVisible) ?? null,
    [circlesData?.circles]
  );

  const { currentMeeting } = useCurrentMeeting();
  const { meetings } = useUserMeetings();
  // currentMeeting can be a recently-ended meeting (within getClosestMeetingToCurrentTime's
  // 24h grace). Fall back to the next future-end meeting so the join CTA stays visible.
  const hasMeeting =
    hasJoinableMeeting(currentMeeting) ||
    getNextUpcomingMeeting({ meetings }) !== null;
  const role = useMemo(() => {
    if (!visibleCircle || !user) return null;
    const circleMeetings = meetings.filter(
      (m) => m.circle === visibleCircle.id
    );
    if (circleMeetings.length === 0) return null;
    const closestMeeting = getClosestMeetingToCurrentTime({
      meetings: circleMeetings,
    });
    if (!closestMeeting) return null;
    return getUserMeetingRoleFromMeeting(closestMeeting, user.id);
  }, [meetings, visibleCircle, user]);

  if (!visibleCircle || !programId) {
    return null;
  }

  return (
    <CohortDashboardBanner
      message={<SchedulingCohortMessageContainer />}
      hasMeeting={hasMeeting}
      role={role}
      onScheduleClick={() => navigate(`/program/${programId}/schedule`)}
      onCohortClick={() => navigate(`/program/${programId}/cohort`)}
      onJoinClick={() => navigate("/video")}
    />
  );
}

export default CohortDashboardBannerContainer;
